import { createApi } from "@reduxjs/toolkit/query/react";
import { showProgress } from "@shared/utils";
import { Property } from "@shared/interfaces/Property";
import { axiosBaseQuery, download } from "@shared/utils/request";
import { ESFilterProductType } from "@shared/constants/presets";
import moment from "moment/moment";

import config from "../../config";
import { ResearchProperty, ResearchConversion } from "./interfaces";

export type GetFiltersParams = {
  productType: ESFilterProductType;
};

export type GetFiltersResponse = {
  dates: string[];
};

export type GetTransactionsParams = {
  state?: string;
  county?: string;
  zip_code?: string;
  neighborhood?: string;
  date?: string;
};

export type GetConversionResponse = {
  monthly: ResearchConversion[];
  selectedTotals: ResearchConversion;
  includeMailingList: boolean;
};

export type GetConversionParams = {
  state?: string;
  county?: string;
  zip_code?: string;
  neighborhood?: string;
  page?: number;
  limit?: number;
  deployListId?: string | number;
  productType: ESFilterProductType;
};

export type GetPropertyListResponse = {
  items: ResearchProperty[];
  total: number;
};

export type GetPropertyListParams = {
  state?: string;
  county?: string;
  zip_code?: string;
  neighborhood?: string;
  page?: number;
  limit?: number;
  isInvestorSale?: boolean;
  isInInvestorList?: boolean;
  isTotal?: boolean;
  deployListId?: number;
  productType: ESFilterProductType;
};

export type GetTransactionResponse = {
  property: Property;
  sales: ResearchProperty;
};

export type GetTransactionParams = {
  transactionId: string;
};

const filterEmptyParams = (params: Record<string, unknown>): Record<string, unknown> => {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
};

export const backTestingApi = createApi({
  reducerPath: "backTestingApi",
  baseQuery: axiosBaseQuery({
    baseUrl: config.baseApiUrl,
  }),
  endpoints: (builder) => ({
    getFilters: builder.query<GetFiltersResponse, GetFiltersParams>({
      query: (params) => {
        return {
          url: "back-testing/filters",
          params,
        };
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
    getConversion: builder.query<GetConversionResponse, GetConversionParams>({
      query: (params) => {
        return {
          url: "back-testing/conversion",
          params: filterEmptyParams(params),
        };
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
    getPropertyList: builder.query<GetPropertyListResponse, GetPropertyListParams>({
      query: (params) => {
        return {
          url: "back-testing/properties",
          params: filterEmptyParams(params),
        };
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
    getPropertiesFile: builder.mutation<Blob, GetPropertyListParams>({
      query: (params) => ({
        url: "back-testing/properties/file",
        method: "GET",
        params,
        responseHandler: (response: Response) => response.blob(),
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        const { data: blob } = await queryFulfilled;

        const url = window.URL.createObjectURL(new Blob([blob]));
        const name = params.isTotal
          ? "total_transactions"
          : params.isInvestorSale
          ? `${params.productType === "INVESTOR" ? "investor" : "realtor"}_transactions`
          : "on_our_list";

        const filename = `${name}_${moment().format("MM-DD-YYYY")}.csv`;

        download(url, filename);
      },
    }),
    getTransaction: builder.query<GetTransactionResponse, GetTransactionParams>({
      query: ({ transactionId }) => {
        return {
          url: `back-testing/transactions/${transactionId}`,
        };
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
  }),
});

export const {
  useGetConversionQuery,
  useGetPropertyListQuery,
  useGetTransactionQuery,
  useGetFiltersQuery,
  useGetPropertiesFileMutation,
} = backTestingApi;
